import { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import { getStorePolicies, PolicyFormat, PolicyType, StorePolicy, StoreSummary, updateStorePolicy } from '../../api/admin';

const POLICY_LABELS: Record<PolicyType, string> = { privacy: '개인정보처리방침', terms: '이용약관' };

interface Props {
  store: StoreSummary;
  onClose: () => void;
}

// 매장별 개인정보처리방침/이용약관. 저장하면 공개 주소(/policy/<번들 ID>/<type>)에 바로 반영된다.
function PolicyPanel({ store, onClose }: Props) {
  const [policies, setPolicies] = useState<Record<PolicyType, StorePolicy> | null>(null);
  const [type, setType] = useState<PolicyType>('privacy');
  const [content, setContent] = useState('');
  const [format, setFormat] = useState<PolicyFormat>('html');
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const fill = (policy: StorePolicy) => {
    setContent(policy.content);
    setFormat(policy.format);
  };

  useEffect(() => {
    setPolicies(null);
    setError(null);
    setMessage(null);
    getStorePolicies(store.id)
      .then((rows) => {
        const byType = Object.fromEntries(rows.map((p) => [p.type, p])) as Record<PolicyType, StorePolicy>;
        setPolicies(byType);
        setType('privacy');
        fill(byType.privacy);
      })
      .catch((err: any) => setError(err.response?.data?.message || '문서를 불러오지 못했습니다.'));
  }, [store.id]);

  const switchType = (next: PolicyType) => {
    if (!policies) return;
    setType(next);
    setError(null);
    setMessage(null);
    fill(policies[next]);
  };

  const handleFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setContent(await file.text());
    setFormat(/\.html?$/i.test(file.name) ? 'html' : 'text');
    setMessage(`${file.name}을(를) 불러왔습니다. 저장해야 반영됩니다.`);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!content.trim() && !window.confirm(`${POLICY_LABELS[type]}을(를) 삭제할까요?`)) return;
    setError(null);
    setMessage(null);
    setSubmitting(true);
    try {
      const updated = await updateStorePolicy(store.id, type, { content, format });
      setPolicies((prev) => (prev ? { ...prev, [type]: { ...prev[type], ...updated } } : prev));
      setMessage(updated.updatedAt ? '저장했습니다.' : '삭제했습니다.');
    } catch (err: any) {
      setError(err.response?.data?.message || '저장에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  const policy = policies?.[type];

  return (
    <section className="StoreManagementScreen-panel">
      <div className="StoreManagementScreen-panelHeader">
        <h2>약관 관리 — {store.name}</h2>
        <button type="button" className="StoreManagementScreen-linkButton" onClick={onClose}>
          닫기
        </button>
      </div>

      <div className="StoreManagementScreen-envTabs">
        {(Object.keys(POLICY_LABELS) as PolicyType[]).map((key) => (
          <button
            key={key}
            type="button"
            className={key === type ? 'active' : ''}
            onClick={() => switchType(key)}
            disabled={!policies}
          >
            {POLICY_LABELS[key]}
            {policies && (policies[key].updatedAt ? ' · 등록됨' : ' · 미등록')}
          </button>
        ))}
      </div>

      {!policies && !error && <p>불러오는 중...</p>}

      {policy && (
        <form className="StoreManagementScreen-form" onSubmit={handleSubmit}>
          <div className="StoreManagementScreen-policyUrl">
            공개 주소{' '}
            <a href={policy.url} target="_blank" rel="noreferrer">
              {policy.url}
            </a>
            <button
              type="button"
              className="StoreManagementScreen-linkButton"
              onClick={() => navigator.clipboard?.writeText(policy.url).then(() => setMessage('주소를 복사했습니다.'))}
            >
              복사
            </button>
          </div>

          <label>
            형식
            <select value={format} onChange={(e) => setFormat(e.target.value as PolicyFormat)}>
              <option value="html">HTML</option>
              <option value="text">텍스트 (줄바꿈 그대로)</option>
            </select>
          </label>
          <label>
            파일 불러오기 (.html, .txt)
            <input type="file" accept=".html,.htm,.txt,text/html,text/plain" onChange={handleFile} />
          </label>

          <textarea
            className="StoreManagementScreen-policyText"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder={`${POLICY_LABELS[type]} 내용을 입력하거나 파일을 불러오세요.`}
            spellCheck={false}
          />

          {error && <p className="StoreManagementScreen-error">{error}</p>}
          {message && <p className="StoreManagementScreen-message">{message}</p>}
          <button type="submit" disabled={submitting}>
            {submitting ? '저장 중...' : `${POLICY_LABELS[type]} 저장`}
          </button>
          {policy.updatedAt && (
            <span className="StoreManagementScreen-meta">마지막 수정 {new Date(policy.updatedAt).toLocaleString()}</span>
          )}
        </form>
      )}

      {!policies && error && <p className="StoreManagementScreen-error">{error}</p>}
    </section>
  );
}

export default PolicyPanel;
